/**
 * Custom JavaScript for theme customizations.
 * Loaded as a module after theme.js in layout/theme.liquid.
 */

/**
 * sale-announcement-bar
 *
 * Dismisses the main sale announcement for one day via localStorage timestamp,
 * then keeps the shared announcement height in sync for sticky headers.
 *
 * First-paint hiding lives in sections/announcement-bar-main-sale.liquid so a
 * dismissed bar never flashes before this deferred module runs.
 */
class SaleAnnouncementBar extends HTMLElement {
  static DEFAULT_DISMISS_TTL_MS = 24 * 60 * 60 * 1000;
  static resizeObserver = null;
  static mutationObserver = null;
  static heightSyncReady = false;

  connectedCallback() {
    this._abortController = new AbortController();
    this.closeButton = this.querySelector('[data-sale-announcement-close]');
    this.dismissKey = this.dataset.dismissKey;
    this.dismissTtl = this._readDismissTtl();

    if (!window.Shopify?.designMode && this._isDismissed()) {
      this._hideBar();
    }

    this.closeButton?.addEventListener('click', this._dismiss.bind(this), {
      signal: this._abortController.signal,
    });

    SaleAnnouncementBar.ensureHeightSync();
    SaleAnnouncementBar.updateAnnouncementHeight();
  }

  disconnectedCallback() {
    this._abortController?.abort();
    window.requestAnimationFrame(() => SaleAnnouncementBar.updateAnnouncementHeight());
  }

  _dismiss() {
    if (!window.Shopify?.designMode && this.dismissKey) {
      try {
        window.localStorage.setItem(this.dismissKey, String(Date.now()));
      } catch (error) {
        // Browsers can disable storage; hiding should still work for this page.
      }
    }

    this._hideBar();
    SaleAnnouncementBar.updateAnnouncementHeight();
    window.dispatchEvent(new Event('resize'));
  }

  _hideBar() {
    this.hidden = true;
    this.closest('.shopify-section')?.setAttribute('hidden', '');
  }

  _readDismissTtl() {
    const ttl = Number(this.dataset.dismissTtl);
    return Number.isFinite(ttl) && ttl > 0 ? ttl : SaleAnnouncementBar.DEFAULT_DISMISS_TTL_MS;
  }

  _isDismissed() {
    if (!this.dismissKey) {
      return false;
    }

    try {
      const rawValue = window.localStorage.getItem(this.dismissKey);
      if (!rawValue) {
        return false;
      }

      const dismissedAt = Number(rawValue);
      if (!Number.isFinite(dismissedAt)) {
        window.localStorage.removeItem(this.dismissKey);
        return false;
      }

      if (Date.now() - dismissedAt < this.dismissTtl) {
        return true;
      }

      window.localStorage.removeItem(this.dismissKey);
      return false;
    } catch (error) {
      return false;
    }
  }

  static ensureHeightSync() {
    if (SaleAnnouncementBar.heightSyncReady) {
      return;
    }

    SaleAnnouncementBar.heightSyncReady = true;

    if (window.ResizeObserver) {
      SaleAnnouncementBar.resizeObserver = new ResizeObserver(() => {
        window.requestAnimationFrame(() => SaleAnnouncementBar.updateAnnouncementHeight());
      });

      SaleAnnouncementBar.observeAnnouncementSections();
    }

    SaleAnnouncementBar.mutationObserver = new MutationObserver(() => {
      SaleAnnouncementBar.observeAnnouncementSections();
      SaleAnnouncementBar.updateAnnouncementHeight();
    });

    SaleAnnouncementBar.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden', 'style', 'class'],
    });

    window.addEventListener('resize', SaleAnnouncementBar.updateAnnouncementHeight, { passive: true });
    document.addEventListener('shopify:section:load', SaleAnnouncementBar.updateAnnouncementHeight);
    document.addEventListener('shopify:section:unload', SaleAnnouncementBar.updateAnnouncementHeight);
  }

  static observeAnnouncementSections() {
    if (!SaleAnnouncementBar.resizeObserver) {
      return;
    }

    document.querySelectorAll('.shopify-section--announcement-bar').forEach((section) => {
      SaleAnnouncementBar.resizeObserver.observe(section);
    });
  }

  static updateAnnouncementHeight() {
    const totalHeight = Array.from(document.querySelectorAll('.shopify-section--announcement-bar'))
      .reduce((height, section) => {
        if (section.hidden || section.closest('[hidden]')) {
          return height;
        }

        return height + section.getBoundingClientRect().height;
      }, 0);

    document.documentElement.style.setProperty('--announcement-bar-height', `${Math.round(totalHeight)}px`);
  }
}

if (!customElements.get('sale-announcement-bar')) {
  customElements.define('sale-announcement-bar', SaleAnnouncementBar);
}

/**
 * Delivery offer: switches delivery text based on selected variant price.
 */
class VariantDelivery extends HTMLElement {
  constructor() {
    super();
    this._onVariantChanged = this._onVariantChanged.bind(this);
  }

  connectedCallback() {
    this._abortController = new AbortController();

    document.forms[this.getAttribute('form')]?.addEventListener('variant:change', this._onVariantChanged, {
      signal: this._abortController.signal,
    });
  }

  disconnectedCallback() {
    this._abortController.abort();
  }

  _onVariantChanged(event) {
    const variant = event.detail.variant;

    if (!variant) return;

    const threshold = parseInt(this.dataset.threshold || '0', 10);
    const deliveryText = threshold > 0 && variant.price >= threshold
      ? this.dataset.freeDelivery || ''
      : this.dataset.paidDelivery || '';

    const deliveryTextElement = this.querySelector('[data-delivery-text]');

    if (deliveryTextElement) {
      deliveryTextElement.textContent = deliveryText;
    }
  }
}

if (!customElements.get('variant-delivery')) {
  customElements.define('variant-delivery', VariantDelivery);
}

/**
 * Custom pagination: Implements infinite scrolling for collection pages.
 */
class CustomPagination extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false

    this.itemsContainer = this.querySelector(".custom-pagination-container");
    this.nextPageLink = this.querySelector(".custom-pagination-link");

    this.nextPageLink.addEventListener('click', this.loadNextPage.bind(this));

    this.observer = new IntersectionObserver(this.loadNextPage.bind(this), { rootMargin: '0px 0px 1000px 0px' })
    this.observer.observe(this.nextPageLink);
  }

  async loadNextPage(entries) {
    if (!entries[0].isIntersecting) return;

    if (this.isLoading) return;
    this.isLoading = true;
    this.nextPageLink.classList.add("custom-pagination-link--loading")

    try {
      const response = await fetch(this.nextPageLink.href);
      if (!response.ok) throw new Error(response.status);

      const template = document.createElement('template');
      template.innerHTML = await response.text();

      let newItemsHtml = '';
      template.content.querySelectorAll('custom-pagination .custom-pagination-item').forEach((result) => {
        newItemsHtml += result.outerHTML;
      });
      this.itemsContainer.insertAdjacentHTML('beforeend', newItemsHtml);

      const newNextPageLink = template.content.querySelector(".custom-pagination-link");
      if (newNextPageLink) {
        this.nextPageLink.href = newNextPageLink.href
      } else {
        this.nextPageLink.remove()
      }
    } catch (error) {
      console.log('Pagination: could not fetch next page - ', error);
    } finally {
      this.isLoading = false;
      this.nextPageLink.classList.remove("custom-pagination-link--loading")
    }
  }
}
customElements.define('custom-pagination', CustomPagination);


/**
 * cart-discount-apply-button
 *
 * Wraps the Apply button in the cart discount field. On click it dispatches
 * a "change" event on the visible discount code input, which triggers the
 * existing CartDiscountField handler in theme.js to submit the code — the
 * same mechanism used by pressing Enter in the input field.
 *
 * Markup pattern (mirrors cart-discount-remove-button):
 *   <cart-discount-apply-button>
 *     <button type="button">Apply</button>
 *   </cart-discount-apply-button>
 */
class CartDiscountApplyButton extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    this._skipSyntheticApply = false;

    this.addEventListener('pointerdown', this._onPointerDown.bind(this), {
      signal: this._abortController.signal,
    });
    this.addEventListener('click', this._applyDiscount.bind(this), {
      signal: this._abortController.signal,
    });
  }

  disconnectedCallback() {
    this._abortController.abort();
  }

  _onPointerDown() {
    const field = this._getVisibleDiscountField();

    // Clicking the button blurs the input, which already triggers the theme's
    // native change handler. Skip our synthetic change in that case.
    this._skipSyntheticApply = field === document.activeElement;
  }

  _getVisibleDiscountField() {
    return this.closest('.cart-discount')?.querySelector(
      'cart-discount-field input:not([type=hidden])'
    );
  }

  _applyDiscount() {
    const field = this._getVisibleDiscountField();

    if (!field) return;

    if (this._skipSyntheticApply) {
      this._skipSyntheticApply = false;
      return;
    }

    field?.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('cart-discount-apply-button')) {
  customElements.define('cart-discount-apply-button', CartDiscountApplyButton);
}

/**
 * one-ultra-modal
 *
 * A centered modal for the One Ultra sizing block.
 * Extends the theme's dialog behavior but appends to <body>
 * so it always layers above sticky headers/navigation.
 */
const BaseDialogElement = window.customElements.get('dialog-element');

if (BaseDialogElement && !window.customElements.get('one-ultra-modal')) {
  class OneUltraModal extends BaseDialogElement {
    connectedCallback() {
      super.connectedCallback();
      this.setAttribute('aria-modal', 'true');
    }

    get shouldAppendToBody() {
      return true;
    }

    get shouldLock() {
      return true;
    }
  }

  window.customElements.define('one-ultra-modal', OneUltraModal);
}

/**
 * smart-ring-atc-modal-guard
 *
 * On PDP forms, opens a confirmation modal before the regular add-to-cart flow
 * for configured smart ring products. Only the main buy-with-quantity submit
 * button is intercepted; sticky ATC and dynamic checkout flows are untouched.
 */
class SmartRingAtcModalGuard extends HTMLElement {
  constructor() {
    super();
    this._pendingForm = null;
    this._bypassGuardForms = new WeakSet();
    this._armedMainAtcForm = null;
    this._modal = null;
    this._handleSubmitCapture = this._handleSubmitCapture.bind(this);
    this._handleClickCapture = this._handleClickCapture.bind(this);
    this._handleModalClick = this._handleModalClick.bind(this);
  }

  connectedCallback() {
    this._abortController = new AbortController();
    this._modal = this._getModal();

    this.addEventListener('click', this._handleClickCapture, {
      capture: true,
      signal: this._abortController.signal,
    });
    this.addEventListener('submit', this._handleSubmitCapture, {
      capture: true,
      signal: this._abortController.signal,
    });

    if (this._modal) {
      this._modal.addEventListener('click', this._handleModalClick, {
        signal: this._abortController.signal,
      });
    }
  }

  disconnectedCallback() {
    this._abortController?.abort();
    this._modal = null;
    this._pendingForm = null;
    this._armedMainAtcForm = null;
  }

  _getModal() {
    const modalId = this.dataset.smartRingModalId;

    if (!modalId) {
      return null;
    }

    const modal = document.getElementById(modalId);

    return modal?.matches('[data-smart-ring-atc-modal]') ? modal : null;
  }

  _handleClickCapture(event) {
    if (!(event.target instanceof Element)) {
      return;
    }

    this._armedMainAtcForm = null;

    const submitButton = event.target.closest('.custom__buy-with-quantity [type="submit"]');

    if (!(submitButton instanceof HTMLElement)) {
      return;
    }

    const form = submitButton.form
      || document.getElementById(submitButton.getAttribute('form') || '');

    if (form instanceof HTMLFormElement) {
      this._armedMainAtcForm = form;
    }
  }

  _handleSubmitCapture(event) {
    const form = event.target;

    if (!(form instanceof HTMLFormElement)) {
      return;
    }

    if (this._bypassGuardForms.has(form)) {
      this._bypassGuardForms.delete(form);
      return;
    }

    const submitter = event.submitter
      || (document.activeElement instanceof HTMLElement ? document.activeElement : null);

    const isArmedMainAtcSubmit = this._armedMainAtcForm === form;
    this._armedMainAtcForm = null;

    const isSubmitterInsideMainAtc = submitter instanceof HTMLElement
      && !!submitter.closest('.custom__buy-with-quantity');

    if (!isArmedMainAtcSubmit && !isSubmitterInsideMainAtc) {
      return;
    }

    const modal = this._modal || this._getModal();

    if (!modal) {
      return;
    }

    const modalFormId = modal.getAttribute('data-smart-ring-form-id');

    if (modalFormId && form.getAttribute('id') !== modalFormId) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    this._pendingForm = form;
    this._openModal(modal);
  }

  _handleModalClick(event) {
    if (!(event.target instanceof Element)) {
      return;
    }

    const confirmButton = event.target.closest('[data-smart-ring-confirm-column] .button');

    if (!confirmButton) {
      return;
    }

    const modal = this._modal || confirmButton.closest('[data-smart-ring-atc-modal]');

    if (!modal) {
      return;
    }

    const form = this._pendingForm;

    if (!form) {
      return;
    }

    event.preventDefault();
    this._pendingForm = null;
    this._closeModal(modal);
    this._bypassGuardForms.add(form);
    form.requestSubmit();
  }

  _openModal(modal) {
    if (typeof modal.show === 'function') {
      modal.show();
      return;
    }

    modal.setAttribute('open', '');
  }

  _closeModal(modal) {
    if (typeof modal.hide === 'function') {
      modal.hide();
      return;
    }

    modal.removeAttribute('open');
  }
}

if (!customElements.get('smart-ring-atc-modal-guard')) {
  customElements.define('smart-ring-atc-modal-guard', SmartRingAtcModalGuard);
}

/**
 * partner-discount-cart-sync
 *
 * Keeps the cart aligned with partner discount rules. When a configured
 * discount code is present, exactly one eligible unit is moved to the
 * partner variant. When no partner code is present, any partner variants
 * in the cart are moved back to their default variants.
 */
class PartnerDiscountCartSync extends HTMLElement {
  static configPromiseByUrl = new Map();
  static currentInstance = null;
  static pendingCart = null;
  static reconciliationState = {
    isRunning: false,
    hasPending: false,
    scheduledTimer: null,
    cooldownUntil: 0,
  };
  static DEBUG = false;
  static ERROR_ICON_SVG = `<svg width="14" viewBox="0 0 18 18">
    <path d="M0 9C0 4.02944 4.02944 0 9 0C13.9706 0 18 4.02944 18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9Z" fill="#eb001b"></path>
    <path d="M5.29289 6.70711L11.2929 12.7071L12.7071 11.2929L6.70711 5.29289L5.29289 6.70711ZM6.70711 12.7071L12.7071 6.70711L11.2929 5.2929L5.29289 11.2929L6.70711 12.7071Z" fill="#ffffff"></path>
  </svg>`;

  connectedCallback() {
    this._abortController = new AbortController();
    PartnerDiscountCartSync.currentInstance = this;

    document.addEventListener('cart:change', this._onCartEvent.bind(this), {
      signal: this._abortController.signal,
    });
    document.addEventListener('cart:refresh', this._onCartRefresh.bind(this), {
      signal: this._abortController.signal,
    });

    PartnerDiscountCartSync.queueReconciliation();
  }

  disconnectedCallback() {
    this._abortController.abort();

    if (PartnerDiscountCartSync.currentInstance === this) {
      PartnerDiscountCartSync.currentInstance = null;
    }
  }

  static queueReconciliation(cart = null) {
    if (cart?.items) {
      PartnerDiscountCartSync.pendingCart = cart;
    }

    const state = PartnerDiscountCartSync.reconciliationState;
    state.hasPending = true;

    if (state.isRunning || state.scheduledTimer) {
      return;
    }

    const delayMs = Math.max(75, state.cooldownUntil - Date.now());
    state.scheduledTimer = window.setTimeout(() => {
      state.scheduledTimer = null;
      PartnerDiscountCartSync._drainQueue().catch((error) => {
        console.error('PartnerDiscountCartSync: reconciliation failed', error);
      });
    }, delayMs);
  }

  static async _drainQueue() {
    const state = PartnerDiscountCartSync.reconciliationState;

    if (state.isRunning) {
      return;
    }

    state.isRunning = true;

    try {
      while (state.hasPending) {
        state.hasPending = false;

        const waitMs = state.cooldownUntil - Date.now();
        if (waitMs > 0) {
          await PartnerDiscountCartSync._wait(waitMs);
        }

        const instance = PartnerDiscountCartSync.currentInstance;
        if (!instance || !instance.isConnected) {
          return;
        }

        const didMutateCart = await instance._reconcile();

        if (didMutateCart) {
          state.cooldownUntil = Date.now() + 400;
        }
      }
    } finally {
      state.isRunning = false;

      if (state.hasPending) {
        PartnerDiscountCartSync.queueReconciliation();
      }
    }
  }

  static _wait(ms) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });
  }

  _onCartEvent(event) {
    PartnerDiscountCartSync.queueReconciliation(event.detail?.cart);
  }

  _onCartRefresh() {
    PartnerDiscountCartSync.queueReconciliation();
  }

  async _reconcile() {
    const [config, cart] = await Promise.all([
      this._loadConfig(),
      this._getCart(),
    ]);

    if (!config || !cart || !Array.isArray(cart.items) || cart.items.length === 0) {
      return false;
    }

    PartnerDiscountCartSync._debug('starting reconciliation', {
      discountCodes: (cart.discount_codes || []).map((discount) => discount.code),
      cartItems: cart.items.map((item) => ({
        key: item.key,
        variant_id: item.variant_id,
        quantity: item.quantity,
        product_title: item.product_title,
        variant_title: item.variant_title,
      })),
    });

    const operations = this._buildOperations(cart, config);
    return this._commitOperations(operations, { cart });
  }

  async _loadConfig() {
    const configUrl = this.dataset.configUrl;

    if (!configUrl) {
      throw new Error('PartnerDiscountCartSync: missing config URL');
    }

    if (!PartnerDiscountCartSync.configPromiseByUrl.has(configUrl)) {
      PartnerDiscountCartSync.configPromiseByUrl.set(
        configUrl,
        fetch(configUrl).then(async (response) => {
          if (!response.ok) {
            throw new Error(`Failed to load config: ${response.status}`);
          }

          return response.json();
        })
      );
    }

    return PartnerDiscountCartSync.configPromiseByUrl.get(configUrl);
  }

  async _getCart() {
    if (PartnerDiscountCartSync.pendingCart?.items) {
      const cart = PartnerDiscountCartSync.pendingCart;
      PartnerDiscountCartSync.pendingCart = null;
      return cart;
    }

    const embeddedCart = this._readEmbeddedCartSnapshot();
    if (embeddedCart?.items) {
      return embeddedCart;
    }

    const response = await fetch(`${window.Shopify.routes.root}cart.js`);
    return response.json();
  }

  _readEmbeddedCartSnapshot() {
    const snapshotElement = this.querySelector('[data-partner-discount-cart-snapshot]');

    if (!snapshotElement?.textContent) {
      return null;
    }

    try {
      return JSON.parse(snapshotElement.textContent);
    } catch (error) {
      console.error('PartnerDiscountCartSync: invalid embedded cart snapshot', error);
      return null;
    }
  }

  _buildOperations(cart, config) {
    const activeScenarioMatch = this._resolveActiveScenarioMatch(cart.discount_codes || [], config);
    const activeScenarioKey = activeScenarioMatch?.scenarioKey || null;
    const partnerVariantMap = config.partner_variant_map || {};
    const activeScenario = activeScenarioKey ? config.scenarios?.[activeScenarioKey] : null;
    const additionsByVariantId = new Map();
    const updatesByLineKey = new Map();
    let hasActivePartnerUnit = false;

    for (const item of cart.items) {
      const variantId = String(item.variant_id);
      const partnerMapping = partnerVariantMap[variantId];

      if (!partnerMapping) {
        continue;
      }

      if (!activeScenario) {
        this._movePartnerItemToDefault(item, partnerMapping, additionsByVariantId, updatesByLineKey);
        continue;
      }

      if (partnerMapping.scenario === activeScenarioKey) {
        if (!hasActivePartnerUnit) {
          hasActivePartnerUnit = true;

          if (item.quantity > 1) {
            this._incrementVariantQuantity(
              additionsByVariantId,
              partnerMapping.default_variant_id,
              item.quantity - 1
            );
            updatesByLineKey.set(item.key, 1);
          }
        } else {
          this._movePartnerItemToDefault(item, partnerMapping, additionsByVariantId, updatesByLineKey);
        }

        continue;
      }

      const activePartnerVariantId = activeScenario.by_default_variant_id?.[
        String(partnerMapping.default_variant_id)
      ];

      if (!hasActivePartnerUnit && activePartnerVariantId) {
        hasActivePartnerUnit = true;
        this._incrementVariantQuantity(additionsByVariantId, activePartnerVariantId, 1);

        if (item.quantity > 1) {
          this._incrementVariantQuantity(
            additionsByVariantId,
            partnerMapping.default_variant_id,
            item.quantity - 1
          );
        }

        updatesByLineKey.set(item.key, 0);
      } else {
        this._movePartnerItemToDefault(item, partnerMapping, additionsByVariantId, updatesByLineKey);
      }
    }

    if (!activeScenario || hasActivePartnerUnit) {
      const operations = {
        activeScenarioMatch,
        additions: this._mapToAdditions(additionsByVariantId),
        updates: Object.fromEntries(updatesByLineKey),
      };

      PartnerDiscountCartSync._debug('planned operations', operations);
      return operations;
    }

    for (const item of cart.items) {
      const partnerVariantId = activeScenario.by_default_variant_id?.[String(item.variant_id)];

      if (!partnerVariantId) {
        continue;
      }

      this._incrementVariantQuantity(additionsByVariantId, partnerVariantId, 1);
      updatesByLineKey.set(item.key, Math.max(0, item.quantity - 1));
      hasActivePartnerUnit = true;
      break;
    }

    const operations = {
      activeScenarioMatch,
      additions: this._mapToAdditions(additionsByVariantId),
      updates: Object.fromEntries(updatesByLineKey),
    };

    PartnerDiscountCartSync._debug('planned operations', operations);
    return operations;
  }

  _resolveActiveScenarioMatch(discountCodes, config) {
    const prefixes = config?.prefixes || {};
    const orderedPrefixes = Array.isArray(config?.ordered_prefixes)
      ? config.ordered_prefixes
      : Object.keys(prefixes).sort(PartnerDiscountCartSync._comparePrefixes);

    PartnerDiscountCartSync._debug('checking discount codes', {
      discountCodes: (discountCodes || []).map((discount) => discount.code),
      orderedPrefixes,
    });

    for (const discount of discountCodes) {
      const code = String(discount.code || '').trim().toLowerCase();

      if (!code) {
        continue;
      }

      const matchedPrefix = orderedPrefixes.find((prefix) => code.startsWith(prefix));
      const scenarioKey = matchedPrefix ? prefixes[matchedPrefix] : null;

      if (scenarioKey) {
        const match = {
          code,
          prefix: matchedPrefix,
          scenarioKey,
        };
        PartnerDiscountCartSync._debug('matched discount code', match);
        return match;
      }
    }

    PartnerDiscountCartSync._debug('no partner discount code matched');
    return null;
  }

  static _comparePrefixes(left, right) {
    return right.length - left.length || left.localeCompare(right);
  }

  static _debug(message, payload) {
    if (!PartnerDiscountCartSync.DEBUG) {
      return;
    }

    if (payload === undefined) {
      console.debug(`PartnerDiscountCartSync: ${message}`);
      return;
    }

    console.debug(`PartnerDiscountCartSync: ${message}`, payload);
  }

  _movePartnerItemToDefault(item, partnerMapping, additionsByVariantId, updatesByLineKey) {
    this._incrementVariantQuantity(
      additionsByVariantId,
      partnerMapping.default_variant_id,
      item.quantity
    );
    updatesByLineKey.set(item.key, 0);
  }

  _incrementVariantQuantity(targetMap, variantId, quantity) {
    if (!variantId || quantity <= 0) {
      return;
    }

    const normalizedVariantId = String(variantId);
    const currentQuantity = targetMap.get(normalizedVariantId) || 0;
    targetMap.set(normalizedVariantId, currentQuantity + quantity);
  }

  _mapToAdditions(additionsByVariantId) {
    return Array.from(additionsByVariantId.entries()).map(([id, quantity]) => ({
      id: Number(id),
      quantity,
    }));
  }

  async _commitOperations({ additions, updates, activeScenarioMatch }, context = {}) {
    const hasAdditions = additions.length > 0;
    const hasUpdates = Object.keys(updates).length > 0;

    if (!hasAdditions && !hasUpdates) {
      PartnerDiscountCartSync._debug('no cart mutations required', {
        activeScenarioMatch,
        additions,
        updates,
      });
      return false;
    }

    const updatedLineKeys = Object.keys(updates);
    this._toggleLineItemLoading(updatedLineKeys, true);

    try {
      if (hasAdditions) {
        const addResponse = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ items: additions }),
        });

        if (!addResponse.ok) {
          const responseText = await addResponse.text();
          const error = new Error(`cart/add.js failed with status ${addResponse.status}`);
          error.details = {
            endpoint: 'cart/add.js',
            status: addResponse.status,
            responseText,
            activeScenarioMatch,
            additions,
            updates,
            discountCodes: (context.cart?.discount_codes || []).map((discount) => discount.code),
            cartItems: (context.cart?.items || []).map((item) => ({
              key: item.key,
              variant_id: item.variant_id,
              quantity: item.quantity,
              product_title: item.product_title,
              variant_title: item.variant_title,
            })),
          };
          this._showLineItemError(
            updatedLineKeys,
            this._extractErrorMessage(responseText) || 'Unable to update cart.'
          );
          PartnerDiscountCartSync._debug('cart/add.js failed', error.details);
          throw error;
        }
      }

      if (hasUpdates) {
        const updateResponse = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ updates }),
        });

        if (!updateResponse.ok) {
          const responseText = await updateResponse.text();
          const error = new Error(`cart/update.js failed with status ${updateResponse.status}`);
          error.details = {
            endpoint: 'cart/update.js',
            status: updateResponse.status,
            responseText,
            activeScenarioMatch,
            additions,
            updates,
          };
          this._showLineItemError(
            updatedLineKeys,
            this._extractErrorMessage(responseText) || 'Unable to update cart.'
          );
          PartnerDiscountCartSync._debug('cart/update.js failed', error.details);
          throw error;
        }
      }

      document.dispatchEvent(new CustomEvent('cart:refresh'));
      return true;
    } catch (error) {
      this._toggleLineItemLoading(updatedLineKeys, false);
      throw error;
    }
  }

  _toggleLineItemLoading(lineKeys, isLoading) {
    if (!Array.isArray(lineKeys) || lineKeys.length === 0) {
      return;
    }

    for (const lineKey of lineKeys) {
      const lineItem = this.closest('cart-drawer')?.querySelector(
        `line-item[data-line-key="${CSS.escape(lineKey)}"]`
      );

      if (!lineItem) {
        continue;
      }

      if (isLoading) {
        this._clearLineItemError(lineItem);
      }

      lineItem.dispatchEvent(new CustomEvent(isLoading ? 'line-item:will-change' : 'line-item:error'));
    }
  }

  _showLineItemError(lineKeys, message) {
    if (!Array.isArray(lineKeys) || lineKeys.length === 0 || !message) {
      return;
    }

    for (const lineKey of lineKeys) {
      const lineItem = this.closest('cart-drawer')?.querySelector(
        `line-item[data-line-key="${CSS.escape(lineKey)}"]`
      );

      if (!lineItem) {
        continue;
      }

      this._clearLineItemError(lineItem);

      const lineItemInfo = lineItem.querySelector('.line-item__info');
      if (!lineItemInfo) {
        continue;
      }

      lineItemInfo.insertAdjacentHTML(
        'beforeend',
        `<p class="text-with-icon text-error text-xs" role="alert">${
          PartnerDiscountCartSync.ERROR_ICON_SVG
        } ${this._escapeHtml(message)}</p>`
      );
    }
  }

  _clearLineItemError(lineItem) {
    lineItem?.querySelector('[role="alert"]')?.remove();
  }

  _extractErrorMessage(responseText) {
    if (!responseText) {
      return null;
    }

    try {
      const responseJson = JSON.parse(responseText);
      return responseJson.description || responseJson.message || null;
    } catch (error) {
      return responseText;
    }
  }

  _escapeHtml(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }
}

if (!customElements.get('partner-discount-cart-sync')) {
  customElements.define('partner-discount-cart-sync', PartnerDiscountCartSync);
}


/**
 * discount-code
 *
 * Copies the discount code to the clipboard when the copy button is clicked.
 * Also supports clicking anywhere on the code span itself.
 * Shows a brief success overlay after copying.
 */
class DiscountCode extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    const opts = { signal: this._abortController.signal };

    const btn = this.querySelector('.discount-code__btn');
    const codeEl = this.querySelector('.discount-code__code');

    if (btn) btn.addEventListener('click', this._copy.bind(this), opts);
    if (codeEl) codeEl.addEventListener('click', this._copy.bind(this), opts);
  }

  disconnectedCallback() {
    this._abortController.abort();
  }

  _copy() {
    const codeEl = this.querySelector('.discount-code__code');
    const successEl = this.querySelector('.discount-code__success');
    if (!codeEl || !successEl) return;

    const code = (codeEl.dataset.discountCode || '').trim();
    if (!code) return;

    navigator.clipboard.writeText(code).then(() => {
      successEl.classList.remove('discount-code__success--hidden');
      setTimeout(() => {
        successEl.classList.add('discount-code__success--hidden');
      }, 2000);
    }).catch(() => {
      const range = document.createRange();
      range.selectNode(codeEl);
      window.getSelection().removeAllRanges();
      window.getSelection().addRange(range);
      document.execCommand('copy');
      window.getSelection().removeAllRanges();

      successEl.classList.remove('discount-code__success--hidden');
      setTimeout(() => {
        successEl.classList.add('discount-code__success--hidden');
      }, 2000);
    });
  }
}

if (!customElements.get('discount-code')) {
  customElements.define('discount-code', DiscountCode);
}

/**
 * desktop-secondary-nav
 *
 * Desktop-only controller for the second-row header navigation.
 * On sticky headers it hides the second row when scrolling down and
 * reveals it when scrolling up, while keeping existing menu behavior.
 */
class DesktopSecondaryNav extends HTMLElement {
  connectedCallback() {
    this.header = this.closest('store-header');
    if (!this.header) return;

    this.desktopMediaQuery = window.matchMedia('(min-width: 1150px)');
    this.isDesktop = this.desktopMediaQuery.matches;
    this.lastScrollPos = window.scrollY;
    this.stickyTransitioning = false;
    this.transitionDurationMs = 200;
    this.tickScheduled = false;

    this.onScroll = this.onScroll.bind(this);
    this.onMediaChange = this.onMediaChange.bind(this);
    this.onToggle = this.onToggle.bind(this);
    this.onResize = this.onResize.bind(this);

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });
    this.desktopMediaQuery.addEventListener('change', this.onMediaChange);
    this.header.addEventListener('toggle', this.onToggle, { capture: true });
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => this.setSecondaryRowHeight());
      this.resizeObserver.observe(this);
    }

    this.setSecondaryRowHeight();
    this.syncState();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    this.desktopMediaQuery?.removeEventListener('change', this.onMediaChange);
    this.resizeObserver?.disconnect();
    this.header?.removeEventListener('toggle', this.onToggle, true);
    this.showSecondaryRow();
    this.header?.style.removeProperty('--header-secondary-row-height');
  }

  onMediaChange(event) {
    this.isDesktop = event.matches;
    this.setSecondaryRowHeight();
    this.syncState();
  }

  onToggle() {
    if (this.hasOpenMenus()) {
      this.showSecondaryRow();
    }
  }

  onResize() {
    this.setSecondaryRowHeight();
  }

  onScroll() {
    if (this.tickScheduled) return;

    this.tickScheduled = true;
    window.requestAnimationFrame(() => {
      this.tickScheduled = false;
      this.updateFromScroll();
    });
  }

  syncState() {
    const currentScrollY = Math.max(0, window.scrollY);
    this.lastScrollPos = currentScrollY;
    this.stickyTransitioning = false;

    if (!this.isDesktop || !this.header.hasAttribute('sticky')) {
      this.showSecondaryRow();
    } else {
      this.updateFromScroll();
    }
  }

  updateFromScroll() {
    if (!this.isDesktop || !this.header.hasAttribute('sticky')) {
      return;
    }

    if (document.body.classList.contains('fixed') || this.hasOpenMenus()) {
      this.showSecondaryRow();
      this.lastScrollPos = Math.max(0, window.scrollY);
      return;
    }

    if (!this.stickyTransitioning) {
      const currentScrollY = Math.max(0, window.scrollY);

      if (currentScrollY < 150) {
        this.showSecondaryRow();
      } else if (this.lastScrollPos < currentScrollY) {
        this.hideSecondaryRow();
      } else if (this.lastScrollPos - 5 > currentScrollY) {
        this.showSecondaryRow();
      }

      this.lastScrollPos = currentScrollY;
    }
  }

  hasOpenMenus() {
    return this.querySelector('[open]') !== null;
  }

  hideSecondaryRow() {
    if (this.stickyTransitioning) return;

    this.header.classList.add('header--secondary-row-hidden');
    this.stickyTransitioning = true;

    window.setTimeout(() => {
      this.lastScrollPos = Math.max(0, window.scrollY);
      this.stickyTransitioning = false;
      this.updateFromScroll();
    }, this.transitionDurationMs);
  }

  showSecondaryRow() {
    this.header.classList.remove('header--secondary-row-hidden');
    this.stickyTransitioning = true;

    window.setTimeout(() => {
      this.lastScrollPos = Math.max(0, window.scrollY);
      this.stickyTransitioning = false;
      this.updateFromScroll();
    }, this.transitionDurationMs);
  }

  setSecondaryRowHeight() {
    if (!this.header) return;
    const rowHeight = Math.ceil(this.scrollHeight || 0);
    if (rowHeight > 0) {
      this.header.style.setProperty('--header-secondary-row-height', `${rowHeight}px`);
    }
  }
}

if (!customElements.get('desktop-secondary-nav')) {
  customElements.define('desktop-secondary-nav', DesktopSecondaryNav);
}

/**
 * main-search-form-controller
 *
 * Keeps the visible main search query free of the internal active product
 * filter token, then appends that token back right before form submission
 * when the active-only mode is selected.
 */
class MainSearchFormController extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.queryInput = this.querySelector('[data-search-display-input]');
    this.modeSelect = this.querySelector('[data-search-mode-select]');
    this.activeProductFilter = this.dataset.activeProductFilter || 'product_type:Aktivní';

    if (!this.form || !this.queryInput || !this.modeSelect) {
      return;
    }

    this._abortController = new AbortController();

    this.form.addEventListener('submit', this._onSubmit.bind(this), {
      signal: this._abortController.signal,
    });
    this.modeSelect.addEventListener('change', this._onModeChanged.bind(this), {
      signal: this._abortController.signal,
    });
  }

  disconnectedCallback() {
    this._abortController?.abort();
  }

  _onSubmit() {
    const displayQuery = this._stripActiveFilter(this.queryInput.value);

    this.queryInput.closest('.search-input')?.classList.add('is-submitting');

    if (this.modeSelect.value === 'active') {
      this.queryInput.value = this._appendActiveFilter(displayQuery);
    } else {
      this.queryInput.value = displayQuery;
    }
  }

  _onModeChanged() {
    if (this.queryInput.value.trim() === '') {
      return;
    }

    this.form.requestSubmit();
  }

  _stripActiveFilter(query) {
    return String(query || '').replace(
      new RegExp(`^${this._escapeRegExp(this.activeProductFilter)}\\s*`),
      ''
    );
  }

  _appendActiveFilter(query) {
    if (!this.activeProductFilter) {
      return query;
    }

    return query ? `${this.activeProductFilter} ${query}` : this.activeProductFilter;
  }

  _escapeRegExp(value) {
    return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}

if (!customElements.get('main-search-form-controller')) {
  customElements.define('main-search-form-controller', MainSearchFormController);
}

/**
 * desktop-header-search
 *
 * Desktop-only shell around the theme's predictive-search component. It keeps
 * the inline header dropdown open while the user is interacting with the field
 * and closes it on outside interaction or escape.
 */
class DesktopHeaderSearch extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.queryInput = this.form?.elements?.q;
    this.dropdown = this.querySelector('.header__desktop-search-dropdown');

    if (!this.form || !this.queryInput || !this.dropdown) {
      return;
    }

    this._abortController = new AbortController();

    this.addEventListener('focusin', this._onFocusIn.bind(this), {
      signal: this._abortController.signal,
    });
    this.addEventListener('keydown', this._onKeyDown.bind(this), {
      signal: this._abortController.signal,
    });
    this.queryInput.addEventListener('input', this._syncOpenState.bind(this), {
      signal: this._abortController.signal,
    });
    this.form.addEventListener('reset', this._onReset.bind(this), {
      signal: this._abortController.signal,
    });
    this.form.addEventListener('submit', this._close.bind(this), {
      signal: this._abortController.signal,
    });
    document.addEventListener('pointerdown', this._onDocumentPointerDown.bind(this), {
      signal: this._abortController.signal,
    });
    document.addEventListener('focusin', this._onDocumentFocusIn.bind(this), {
      signal: this._abortController.signal,
    });
  }

  disconnectedCallback() {
    this._abortController?.abort();
  }

  _onFocusIn() {
    this._syncOpenState();
  }

  _onKeyDown(event) {
    if (event.key !== 'Escape') return;

    this._close();
    this.queryInput.blur();
  }

  _onReset() {
    window.setTimeout(() => this._close(), 0);
  }

  _onDocumentPointerDown(event) {
    if (this.contains(event.target)) return;

    this._close();
  }

  _onDocumentFocusIn(event) {
    if (this.contains(event.target)) return;

    this._close();
  }

  _syncOpenState() {
    if (this._hasQuery()) {
      this._open();
    } else {
      this._close();
    }
  }

  _hasQuery() {
    return this.queryInput.value.trim() !== '';
  }

  _open() {
    this.classList.add('is-open');
    this.dropdown.hidden = false;
  }

  _close() {
    this.classList.remove('is-open');
    this.dropdown.hidden = true;
  }
}

if (!customElements.get('desktop-header-search')) {
  customElements.define('desktop-header-search', DesktopHeaderSearch);
}

/**
 * Customization: keep the active PDP tab label fully visible on mobile,
 * but only after the shopper starts interacting with the tabs.
 */
class ProductTabsMobileScroll extends HTMLElement {
  constructor() {
    super();

    this.mobileMediaQuery = window.matchMedia('(max-width: 699px)');
    this.hasInteracted = false;
    this.onClick = this.onClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.observer = new MutationObserver((mutations) => {
      if (!this.hasInteracted) return;

      for (const mutation of mutations) {
        if (
          mutation.type === 'attributes'
          && mutation.attributeName === 'aria-selected'
          && mutation.target.matches('.tabs-nav__button[aria-selected="true"]')
        ) {
          this.scrollTabButtonIntoView(mutation.target);
          break;
        }
      }
    });
  }

  connectedCallback() {
    this.tabsElement = this.querySelector('x-tabs');
    if (!this.tabsElement) return;

    this.ensureAccessibleTabList();
    this.tabsElement.addEventListener('click', this.onClick);
    this.tabsElement.addEventListener('keydown', this.onKeyDown);
    this.observer.observe(this.tabsElement, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-selected'],
    });
  }

  disconnectedCallback() {
    this.tabsElement?.removeEventListener('click', this.onClick);
    this.tabsElement?.removeEventListener('keydown', this.onKeyDown);
    this.observer.disconnect();
  }

  onClick(event) {
    const clickedButton = event.target.closest('.tabs-nav__button');
    if (!clickedButton) return;

    this.hasInteracted = true;
    window.requestAnimationFrame(() => this.scrollTabButtonIntoView(clickedButton));
  }

  onKeyDown(event) {
    const focusedButton = event.target.closest('.tabs-nav__button');
    if (!focusedButton) return;

    const activationKeys = ['Enter', ' ', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!activationKeys.includes(event.key)) return;

    this.hasInteracted = true;
  }

  // Accessibility enhancements
  ensureAccessibleTabList() {
    const shadowRoot = this.tabsElement?.shadowRoot;
    const titleSlot = shadowRoot?.querySelector('slot[name="title"]');
    if (!titleSlot) return;

    this.tabsElement.removeAttribute('role');
    this.tabsElement.removeAttribute('aria-label');
    titleSlot.removeAttribute('role');
    titleSlot.removeAttribute('part');

    let tabList = titleSlot.parentElement;
    if (!tabList?.matches('[data-product-tabs-tablist]')) {
      tabList = document.createElement('div');
      tabList.setAttribute('data-product-tabs-tablist', '');
      titleSlot.replaceWith(tabList);
      tabList.appendChild(titleSlot);
    }

    tabList.setAttribute('role', 'tablist');
    tabList.setAttribute('part', 'tab-list');

    const tabListLabel = this.tabsElement.dataset.tablistLabel?.trim();
    if (tabListLabel) {
      tabList.setAttribute('aria-label', tabListLabel);
    } else {
      tabList.removeAttribute('aria-label');
    }
  }

  scrollTabButtonIntoView(button, behavior = 'smooth') {
    if (!button || !this.mobileMediaQuery.matches) return;

    button.scrollIntoView({
      behavior,
      block: 'nearest',
      inline: 'center',
    });
  }
}

if (!customElements.get('product-tabs-mobile-scroll')) {
  customElements.define('product-tabs-mobile-scroll', ProductTabsMobileScroll);
}

/**
 * Customization: mobile facet floating filter visibility.
 *
 * Keep theme.js untouched; control visibility via classes:
 * - hide floating button until the top trigger is scrolled past
 * - hide near footer
 */
function initMobileFacetFloatingFilterVisibility() {
  const floatingFilters = document.querySelectorAll('facet-floating-filter[trigger-selector]');

  floatingFilters.forEach((floatingFilter) => {
    const section = floatingFilter.closest('.shopify-section');
    const triggerSelector = floatingFilter.getAttribute('trigger-selector') || '[data-facet-top-trigger]';
    const topTrigger = section?.querySelector(triggerSelector) || document.querySelector(triggerSelector);
    const footer = document.querySelector('.shopify-section--footer');

    floatingFilter._mobileFacetCleanup?.();

    const syncVisibility = () => {
      const topTriggerPassed = topTrigger ? topTrigger.getBoundingClientRect().bottom < 0 : true;
      const footerVisible = footer
        ? (footer.getBoundingClientRect().top < window.innerHeight && footer.getBoundingClientRect().bottom > 0)
        : false;
      const nearTopOfPage = window.scrollY < 24;
      const shouldShow = !nearTopOfPage && topTriggerPassed && !footerVisible;

      floatingFilter.classList.toggle('facet-floating-filter--custom-visible', shouldShow);
      floatingFilter.classList.toggle('facet-floating-filter--custom-hidden', !shouldShow);
    };

    const onScrollOrResize = () => {
      window.requestAnimationFrame(syncVisibility);
    };

    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize, { passive: true });

    syncVisibility();

    floatingFilter._mobileFacetCleanup = () => {
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
    };
  });
}

document.addEventListener('DOMContentLoaded', initMobileFacetFloatingFilterVisibility);
document.addEventListener('shopify:section:load', initMobileFacetFloatingFilterVisibility);
document.addEventListener('facet:update', () => {
  window.setTimeout(initMobileFacetFloatingFilterVisibility, 250);
});

/**
 * collection-tabs-carousel
 *
 * Handles collection tab switching inside the custom tabbed carousel section.
 */
class CollectionTabsCarousel extends HTMLElement {
  connectedCallback() {
    this.tabTriggers = Array.from(this.querySelectorAll('[data-tab-trigger]'));
    this.tabPanels = Array.from(this.querySelectorAll('[data-tab-panel]'));
    this.tabLinks = Array.from(this.querySelectorAll('[data-tab-link]'));
    this.backgroundLayers = Array.from(this.querySelectorAll('[data-tab-background]'));
    this.visualItems = Array.from(this.querySelectorAll('[data-tab-image]'));

    if (this.tabTriggers.length === 0 || this.tabPanels.length === 0) {
      return;
    }

    this._onTabClick = this._onTabClick.bind(this);
    this._onBlockSelect = this._onBlockSelect.bind(this);
    this.addEventListener('click', this._onTabClick);
    document.addEventListener('shopify:block:select', this._onBlockSelect);

    const initiallySelected = this.tabTriggers.find((trigger) => trigger.getAttribute('aria-selected') === 'true');
    this._activateTab(initiallySelected?.dataset.tabId || this.tabTriggers[0].dataset.tabId, false);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onTabClick);
    document.removeEventListener('shopify:block:select', this._onBlockSelect);
  }

  _onTabClick(event) {
    const trigger = event.target.closest('[data-tab-trigger]');

    if (!trigger || !this.contains(trigger)) {
      return;
    }

    this._activateTab(trigger.dataset.tabId, true);
  }

  _onBlockSelect(event) {
    const blockElement = event.target;

    if (!(blockElement instanceof Element) || !this.contains(blockElement)) {
      return;
    }

    const trigger = blockElement.matches('[data-tab-trigger]')
      ? blockElement
      : this.querySelector(`[data-tab-trigger][data-tab-id="${blockElement.getAttribute('data-tab-id') || ''}"]`);

    if (!trigger) {
      return;
    }

    this._activateTab(trigger.dataset.tabId, false);
  }

  _activateTab(tabId, shouldFocus) {
    if (!tabId) return;

    const activeTrigger = this.tabTriggers.find((trigger) => trigger.dataset.tabId === tabId);

    this.tabTriggers.forEach((trigger) => {
      const isActive = trigger.dataset.tabId === tabId;
      trigger.classList.toggle('is-active', isActive);
      trigger.setAttribute('aria-selected', String(isActive));

      if (isActive && shouldFocus) {
        trigger.focus({ preventScroll: true });
      }
    });

    this.tabPanels.forEach((panel) => {
      const isActive = panel.dataset.tabId === tabId;
      panel.classList.toggle('is-active', isActive);
      panel.toggleAttribute('hidden', !isActive);
    });

    this.tabLinks.forEach((link) => {
      const isActive = link.dataset.tabId === tabId;
      link.toggleAttribute('hidden', !isActive || !link.getAttribute('href'));
    });

    this.backgroundLayers.forEach((layer) => {
      layer.classList.toggle('is-active', layer.dataset.tabId === tabId);
    });

    this.visualItems.forEach((item) => {
      item.classList.toggle('is-active', item.dataset.tabId === tabId);
    });

    if (activeTrigger) {
      this.dataset.tone = activeTrigger.dataset.contentTone || 'dark';
    }

    window.requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
  }
}

if (!customElements.get('collection-tabs-carousel')) {
  customElements.define('collection-tabs-carousel', CollectionTabsCarousel);
}

/**
 * collection-description-toggle
 *
 * Measures the rendered mobile description height and only enables the
 * collapsed "show more / less" UI when the content actually overflows.
 */
class CollectionDescriptionToggle extends HTMLElement {
  constructor() {
    super();
    this._onToggleClick = this._onToggleClick.bind(this);
    this._updateOverflowState = this._updateOverflowState.bind(this);
  }

  connectedCallback() {
    this.content = this.querySelector('[data-description-content]');
    this.toggleButton = this.querySelector('[data-description-toggle]');

    if (!this.content || !this.toggleButton) {
      return;
    }

    this.mobileMediaQuery = window.matchMedia('(max-width: 699px)');
    this.expandLabel = this.toggleButton.dataset.labelExpand || this.toggleButton.textContent.trim();
    this.collapseLabel = this.toggleButton.dataset.labelCollapse || this.expandLabel;
    this.toggleButton.addEventListener('click', this._onToggleClick);
    window.addEventListener('resize', this._updateOverflowState, { passive: true });

    window.requestAnimationFrame(this._updateOverflowState);
  }

  disconnectedCallback() {
    this.toggleButton?.removeEventListener('click', this._onToggleClick);
    window.removeEventListener('resize', this._updateOverflowState);
  }

  _onToggleClick() {
    if (this.toggleButton.hidden) {
      return;
    }

    this.toggleAttribute('expanded');
    this._render();
  }

  _updateOverflowState() {
    const collapsedHeight = this._getCollapsedHeight();
    const shouldCollapse = this.mobileMediaQuery.matches
      && collapsedHeight > 0
      && this.content.scrollHeight > collapsedHeight + 1;

    this.toggleAttribute('data-overflow', shouldCollapse);
    this.toggleButton.hidden = !shouldCollapse;

    if (!shouldCollapse) {
      this.removeAttribute('expanded');
    }

    this._render();
  }

  _render() {
    const isExpanded = this.hasAttribute('expanded');
    this.toggleButton.textContent = isExpanded ? this.collapseLabel : this.expandLabel;
    this.toggleButton.setAttribute('aria-expanded', String(isExpanded));
  }

  _getCollapsedHeight() {
    const collapsedHeightValue = getComputedStyle(this)
      .getPropertyValue('--collection-description-collapsed-height')
      .trim();

    if (!collapsedHeightValue) {
      return 0;
    }

    if (collapsedHeightValue.endsWith('rem')) {
      const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize);
      return Number.parseFloat(collapsedHeightValue) * rootFontSize;
    }

    if (collapsedHeightValue.endsWith('px')) {
      return Number.parseFloat(collapsedHeightValue);
    }

    return Number.parseFloat(collapsedHeightValue) || 0;
  }
}

if (!customElements.get('collection-description-toggle')) {
  customElements.define('collection-description-toggle', CollectionDescriptionToggle);
}
